import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Alert,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useVenuesApi, useStagesApi } from "@/context/AppContext";
import { Breadcrumbs } from "@/utils/components/Breadcrumbs";
import type { VenueList, Stage } from "api-client/models";
import { getSelectedOptionId } from '@/utils/selectionDefaults';

interface StageFormData {
  name: string;
  capacity: number | "";
  stageType: string;
  technicalSpecs: {
    sound_system?: string;
    lighting?: string;
    stage_dimensions?: string;
    load_in_notes?: string;
  };
  venue: string;
}

const STAGE_TYPE_CHOICES = [
  { value: "indoor", label: "Indoor" },
  { value: "outdoor", label: "Outdoor" },
  { value: "side", label: "Side Stage" },
  { value: "amphitheater", label: "Amphitheater" },
  { value: "studio", label: "Studio" },
  { value: "bar_stage", label: "Bar Stage" },
  { value: "other", label: "Other" },
];

const initialFormData: StageFormData = {
  name: "",
  capacity: "",
  stageType: "",
  technicalSpecs: {},
  venue: "",
};

export const StageForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const venuesApi = useVenuesApi();
  const stagesApi = useStagesApi();
  const [formData, setFormData] = useState<StageFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [checkingLimit, setCheckingLimit] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [venues, setVenues] = useState<VenueList[]>([]);

  const isEditMode = Boolean(id);
  const venueIdFromQuery = searchParams.get("venue");

  useEffect(() => {
    fetchVenues();
  }, []);

  useEffect(() => {
    if (!isEditMode && venueIdFromQuery) {
      checkVenueStageLimit(venueIdFromQuery);
    } else {
      setCheckingLimit(false);
    }
  }, [venueIdFromQuery, isEditMode]);

  useEffect(() => {
    if (isEditMode) {
      fetchStage();
    }
  }, [id]);

  const fetchVenues = async () => {
    try {
      const response = await venuesApi.venuesList();
      const venueList = response.results || [];
      setVenues(venueList);

      if (!isEditMode) {
        setFormData((prev) => ({
          ...prev,
          venue: getSelectedOptionId(venueList, venueIdFromQuery || prev.venue),
        }));
      }
    } catch {
      setError("Failed to load venues.");
    }
  };

  const checkVenueStageLimit = async (venueId: string) => {
    try {
      const response = await stagesApi.stagesList();
      const stages = response.results || [];
      const venueStages = stages.filter((s: Stage) => s.venue === venueId);

      if (venueStages.length >= 1) {
        navigate("/stages", {
          replace: true,
          state: {
            message:
              "This venue already has a stage. Contact Solid Slate to add additional stages.",
          },
        });
        return;
      }
    } catch {
      setError("Failed to check stage limit.");
    } finally {
      setCheckingLimit(false);
    }
  };

  const fetchStage = async () => {
    try {
      setLoading(true);
      const stage = await stagesApi.stagesRetrieve({ id: id! });

      setFormData({
        name: stage.name || "",
        capacity: stage.capacity || "",
        stageType: stage.stageType || "",
        technicalSpecs: stage.technicalSpecs || {},
        venue: stage.venue || "",
      });
    } catch {
      setError("Failed to load stage. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.venue) {
      setError("Please select a venue for this stage.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payload = {
        name: formData.name,
        capacity: formData.capacity === "" ? null : Number(formData.capacity),
        stage_type: formData.stageType,
        technical_specs: formData.technicalSpecs,
        venue: formData.venue,
        subscription_status: "active", // Default to active
      };

      if (isEditMode) {
        await stagesApi.stagesPartialUpdate({
          id: id!,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          patchedStage: payload as any, // OpenAPI schema accepts snake_case
        });
      } else {
        await stagesApi.stagesCreate({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          stage: payload as any, // OpenAPI schema accepts snake_case
        });
      }

      navigate("/stages");
    } catch {
      setError("Failed to save stage. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <Stack alignItems="center" mt={4}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading stage...</Typography>
      </Stack>
    );
  }

  if (checkingLimit) {
    return (
      <Stack alignItems="center" mt={4}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Checking stage limit...</Typography>
      </Stack>
    );
  }

  return (
    <Box>
      <Breadcrumbs
        items={
          isEditMode
            ? [
                { label: "Stages", path: "/stages" },
                { label: formData.name || "Loading...", path: `/stages/${id}` },
                { label: "Edit" },
              ]
            : [{ label: "Stages", path: "/stages" }, { label: "New Stage" }]
        }
      />
      <Typography variant="h4" gutterBottom>
        {isEditMode ? "Edit Stage" : "New Stage"}
      </Typography>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {error && <Alert severity="error">{error}</Alert>}

              {/* Venue Selection */}
              <TextField
                select
                label="Venue"
                required
                fullWidth
                value={formData.venue}
                onChange={(e) =>
                  setFormData({ ...formData, venue: e.target.value })
                }
                disabled={isEditMode} // Can't change venue after creation
                helperText={
                  isEditMode
                    ? "Venue cannot be changed after creation"
                    : "Select the venue for this stage"
                }
              >
                {venues.map((venue) => (
                  <MenuItem key={venue.id} value={venue.id}>
                    {venue.name}
                  </MenuItem>
                ))}
              </TextField>

              {/* Basic Information */}
              <Typography variant="h6">Basic Information</Typography>

              <TextField
                label="Stage Name"
                required
                fullWidth
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                helperText="e.g., 'Main Stage', 'Bar Stage', 'Outdoor Stage'"
              />

              <TextField
                label="Capacity"
                type="number"
                required
                fullWidth
                value={formData.capacity}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    capacity:
                      e.target.value === "" ? "" : Number(e.target.value),
                  })
                }
                helperText="Maximum audience capacity for this stage"
              />

              <TextField
                select
                label="Stage Type"
                required
                fullWidth
                value={formData.stageType}
                onChange={(e) =>
                  setFormData({ ...formData, stageType: e.target.value })
                }
              >
                {STAGE_TYPE_CHOICES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              {/* Technical Specifications */}
              <Typography variant="h6">Technical Specifications</Typography>

              <TextField
                label="Sound System"
                fullWidth
                multiline
                rows={2}
                value={formData.technicalSpecs.sound_system || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    technicalSpecs: {
                      ...formData.technicalSpecs,
                      sound_system: e.target.value,
                    },
                  })
                }
                helperText="PA system details, speakers, monitors"
              />

              <TextField
                label="Lighting"
                fullWidth
                multiline
                rows={2}
                value={formData.technicalSpecs.lighting || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    technicalSpecs: {
                      ...formData.technicalSpecs,
                      lighting: e.target.value,
                    },
                  })
                }
                helperText="Lighting equipment and capabilities"
              />

              <TextField
                label="Stage Dimensions"
                fullWidth
                value={formData.technicalSpecs.stage_dimensions || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    technicalSpecs: {
                      ...formData.technicalSpecs,
                      stage_dimensions: e.target.value,
                    },
                  })
                }
                helperText="e.g., '24ft x 16ft' or '7m x 5m'"
              />

              <TextField
                label="Load-In Notes"
                fullWidth
                multiline
                rows={3}
                value={formData.technicalSpecs.load_in_notes || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    technicalSpecs: {
                      ...formData.technicalSpecs,
                      load_in_notes: e.target.value,
                    },
                  })
                }
                helperText="Access instructions, restrictions, parking details"
              />

              {/* Actions */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button type="submit" variant="contained" disabled={loading}>
                  {loading
                    ? "Saving..."
                    : isEditMode
                      ? "Update Stage"
                      : "Create Stage"}
                </Button>
                <Button variant="outlined" onClick={() => navigate("/stages")}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
